import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, Video, Users, Wrench, Mic, ShoppingBag, Lock } from "lucide-react";
import Seo from "../components/Seo";

const OFFERS = [
  { icon: BookOpen, image: "/practice/classroom.jpg", title: "Templates & Toolbox", body: "Editable, compliance-ready templates for baselining, engagement, impact prediction and reporting.", to: "/shop" },
  { icon: Video, image: "/practice/library.jpg", title: "On-demand training", body: "Structured modules covering scoping, data analysis, management, and defensible reporting.", to: "/shop" },
  { icon: Users, image: "/practice/community.jpg", title: "Community of practice", body: "Bi-monthly live forums with peers and Andrea Kanaris - plus a private practitioner network.", to: "/shop" },
  { icon: Wrench, image: "/practice/coaching.webp", title: "Coaching", body: "1:1 mentoring for graduates, consultants and internal ESG teams.", to: "/shop" },
  { icon: Mic, image: "/practice/podcast.jpg", title: "Podcast & videos", body: "Voices of Change and long-form interviews - free and searchable.", to: "/podcast" },
  { icon: ShoppingBag, image: "/practice/book-cover.webp", imageFit: "contain", imageBg: "bg-slate-100", title: "Book & shop", body: "A Practical Guide to Social Impact Assessment for the Real World - by Andrea Kanaris. Plus specialist merchandise.", to: "/shop" },
];

export default function Practitioners() {
  const navigate = useNavigate();
  return (
    <div data-testid="practitioners-page">
      <Seo
        path="/practitioners"
        title="For Practitioners - Toolbox, Resources & Community"
        description="A practitioner-focused hub for social impact professionals: 12-module SIA Toolbox, private resources, Andrea's podcast and community access."
      />
      <section className="relative bg-brand-blue text-white py-24 lg:py-32 overflow-hidden">
        <img src="/practice/community.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/85 to-brand-blue/30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="eyebrow text-brand-yellow mb-6">For Practitioners</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">Learn SIA the way it is actually practised.</h1>
            <p className="text-lg lg:text-xl text-white/85 max-w-3xl mt-6 leading-relaxed">A structured platform of templates, on-demand training, community, coaching and Andrea's book - designed for graduates, consultants and in-house ESG teams building real-world capability.</p>
            <div className="flex flex-wrap gap-4 mt-10">
              <button
                data-testid="explore-membership-btn"
                onClick={() => navigate("/shop")}
                className="btn-primary"
              >
                Explore Membership <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="member-hub-btn"
                onClick={() => navigate("/member/hub")}
                className="btn-secondary bg-white/10 border-white/40 text-white hover:bg-white hover:text-brand-blue"
              >
                <Lock className="w-4 h-4" /> Access the Member Hub
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-14 max-w-2xl">
            <div className="eyebrow mb-4">What's inside</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-brand-blue leading-tight">Everything you need to deliver social impact in the real world.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {OFFERS.map((o) => {
              const inner = (
                <>
                  <div className={`relative h-48 overflow-hidden ${o.imageBg || ""}`}>
                    <img src={o.image} alt="" className={`w-full h-full ${o.imageFit === "contain" ? "object-contain p-3" : "object-cover"} group-hover:scale-105 transition-transform duration-700`} />
                    <div className="absolute top-4 left-4 w-11 h-11 bg-white/95 flex items-center justify-center">
                      <o.icon className="w-6 h-6 text-brand-orange" />
                    </div>
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <h3 className="font-serif text-xl text-brand-blue mb-3">{o.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{o.body}</p>
                  </div>
                </>
              );
              const classes = "group border border-slate-200 hover:border-brand-orange transition-colors overflow-hidden flex flex-col";
              return o.to
                ? <Link key={o.title} to={o.to} data-testid={`offer-${o.title.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`} className={classes}>{inner}</Link>
                : <div key={o.title} className={classes}>{inner}</div>;
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">Coming soon</div>
            <h2 className="font-serif text-3xl lg:text-4xl text-brand-blue leading-tight mb-6">A dedicated Member Resource Hub.</h2>
            <p className="text-slate-600 leading-relaxed mb-6">A secure, mobile-friendly hub with dashboard, search, categories, filters, bookmarks, new-and-updated labels, document previews, downloads, and account/billing controls. Public preview pages will exist for selected resources so you can see the shape of what is inside before subscribing.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { k: "Public", v: "Open articles, insights, podcast" },
                { k: "Free member", v: "Registered practitioners with entitlements" },
                { k: "Paid member", v: "Full templates, live forums, coaching add-ons" },
              ].map((t) => (
                <div key={t.k} className="bg-white border border-slate-200 p-5">
                  <div className="text-xs uppercase tracking-[0.2em] text-brand-orange font-semibold mb-2">{t.k}</div>
                  <div className="text-sm text-slate-700">{t.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden">
              <img src="/practice/coaching.webp" alt="Practitioner coaching session" className="w-full h-64 object-cover" />
            </div>
            <div className="bg-brand-blue-deep text-white p-10 mt-4">
              <div className="eyebrow text-brand-yellow mb-4">Not a practitioner?</div>
              <h3 className="font-serif text-2xl leading-snug mb-4">You are probably looking for the advisory practice.</h3>
              <p className="text-white/80 text-sm mb-6">If you represent a project, board or agency, our advisory arm is the right pathway.</p>
              <Link to="/contact" className="btn-primary">Contact Us <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
